import { useReducer } from "react";
import ReportContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import { ICityQuery } from "../../models/city/query";
import {
  ICreatePurchaseForecastReport,
  ICreateReport,
} from "../../models/report/request";
import { successNotification } from "../../utils/helpers/notification";
import { t } from "i18next";
import { IReport } from "../../models/report/response";

export interface IProps {
  children: React.ReactNode;
}
const ReportContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);
  const terminalStatuses = [
    "available",
    "confirmed",
    "failed",
    "expired",
    "cancelled",
  ];

  const getData = async () => {
    let result;

    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.report.getAllReports(state.query);
        result = data;

        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });

    return result;
  };

  const pollReportUntilReady = (createdReport?: IReport) => {
    if (!createdReport?.id) return;

    let attempts = 0;
    const maxAttempts = 30;

    const poll = async () => {
      attempts += 1;
      const data = await getData();
      const report = data?.data?.find((item) => item.id === createdReport.id);
      const status = report?.status;

      if (
        attempts < maxAttempts &&
        (!status || !terminalStatuses.includes(status))
      ) {
        window.setTimeout(poll, 10000);
      }
    };

    window.setTimeout(poll, 10000);
  };

  const createReport = async (request: ICreateReport, path: string) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });

        const { data } = await EndPoints.report.createReport(request, path);
        await successNotification(t("report_is_being_prepared"));
        await getData();
        pollReportUntilReady(data);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
      },
      throwException: true,
    });
  };

  const createLowStockReport = async (request: ICreateReport) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });

        const { data } = await EndPoints.report.createLowStockReport(request);
        await successNotification(t("report_is_being_prepared"));
        await getData();
        pollReportUntilReady(data);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
      },
      throwException: true,
    });
  };

  const createPurchaseForecastReport = async (
    request: ICreatePurchaseForecastReport,
  ) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });

        const { data } =
          await EndPoints.report.createPurchaseForecastReport(request);
        await successNotification(t("report_is_being_prepared"));
        await getData();
        pollReportUntilReady(data);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
      },
      throwException: true,
    });
  };

  const clearLowStockData = () => {
    dispatch({ type: "CLEAR_LOW_STOCK_DATA" });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: ICityQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <ReportContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          createReport,
          createLowStockReport,
          createPurchaseForecastReport,
          clearLowStockData,
          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </ReportContext.Provider>
  );
};

export default ReportContextProvider;
